import {format} from 'date-fns'
import {useTranslation} from '@/translations/i18n'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Text} from '@/components/ui/text/Text'
import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {ConfirmationDialog} from '@/components/ui/confirmation-dialog-atoms/ConfirmationDialogAtoms'
import {FormProvider, useForm} from 'react-hook-form'
import {useStoreEvent} from '../../queries/useStoreEvent'
import {AddConsultantAbsenceConfirmationValidationSchema} from '../../formSchemas'
import {DialogStore, useDialogContext} from '@ariakit/react'
import {consultantAbsenceFormValuesToPayload} from '../../utils'
import {absenceReasonToLabel} from '@/features/users/utils'
import {DialogHeading} from '@/components/ui/dialog-atoms/DialogAtoms'

export const ConsultantAbsenceConfirmationDialog = ({
    defaultValues,
    dialogStore
}: {
    defaultValues: AddConsultantAbsenceConfirmationValidationSchema
    dialogStore: DialogStore
}) => {
    const {t} = useTranslation()

    const createDialogStore = useDialogContext()
    if (!createDialogStore) {
        throw new Error('createDialogStore is not found')
    }

    const {mutate: createEvent, isPending} = useStoreEvent({
        onSuccess: () => {
            createDialogStore.setOpen(false)
        }
    })

    const form = useForm<AddConsultantAbsenceConfirmationValidationSchema>({
        defaultValues: defaultValues
            ? {
                  ...defaultValues,
                  notificationsRecipients: defaultValues.consultant
                      ? [{id: defaultValues.consultant.id, fullName: defaultValues.consultant.fullName}]
                      : []
              }
            : undefined
    })
    const onSubmit = form.handleSubmit(
        data => {
            createEvent(consultantAbsenceFormValuesToPayload(data))
        },
        errors => {
            console.error('Validation errors:', errors)
        }
    )

    const values = form.watch()
    const consultant = values.consultant

    return (
        <FormProvider {...form}>
            <ConfirmationDialog
                dialogStore={dialogStore}
                onConfirm={onSubmit}
                isConfirmPending={isPending}
                headerSlot={
                    <Flexbox direction="column" gap={7} fullWidth>
                        <Flexbox direction="column" fullWidth gap={1}>
                            <DialogHeading>{t('agenda:add_consultant_absence_confirmation_title')}</DialogHeading>
                            <Text palette="neutral" shade="500" size="textSm">
                                {`${consultant?.fullName} ${consultant?.specializationName ? `(${consultant.specializationName})` : ''}`}
                            </Text>
                        </Flexbox>
                        <Grid columns={4} columnGap={6} rowGap={2} fullWidth>
                            <Text palette="neutral" shade="500" size="textSm">
                                {t('agenda:confirmation_start_date')}
                            </Text>
                            <Text palette="neutral" shade="900" size="textSm" weight="500">
                                {values.startDate && format(values.startDate, 'dd/MM/yyyy')}
                            </Text>
                            <Text palette="neutral" shade="500" size="textSm">
                                {t('agenda:confirmation_start_time')}
                            </Text>
                            <Text palette="neutral" shade="900" size="textSm" weight="500">
                                {values.startTime || '-'}
                            </Text>
                            <Text palette="neutral" shade="500" size="textSm">
                                {t('agenda:confirmation_end_date')}
                            </Text>
                            <Text palette="neutral" shade="900" size="textSm" weight="500">
                                {values.endDate && format(values.endDate, 'dd/MM/yyyy')}
                            </Text>
                            <Text palette="neutral" shade="500" size="textSm">
                                {t('agenda:confirmation_end_time')}
                            </Text>
                            <Text palette="neutral" shade="900" size="textSm" weight="500">
                                {values.endTime || '-'}
                            </Text>
                            <Text palette="neutral" shade="500" size="textSm">
                                {t('agenda:reason')}
                            </Text>
                            <GridItem spanColumn={3}>
                                <Text palette="neutral" shade="900" size="textSm" weight="500">
                                    {values.absenceReason ? t(absenceReasonToLabel[values.absenceReason]) : '-'}
                                </Text>
                            </GridItem>
                        </Grid>
                    </Flexbox>
                }
            />
        </FormProvider>
    )
}
